// src/components/GatheringDetailContent.tsx
import React, { useState } from 'react';
import { 
  Typography, 
  Divider, 
  Chip, 
  Box, 
  Paper,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Avatar,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Import Grid directly as per MUI v7 pattern
import { format, parseISO } from 'date-fns';
import { Gathering, GatheringStatus, GatheringCheckIn } from '../../../../types';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import NotesIcon from '@mui/icons-material/Notes';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import BuildIcon from '@mui/icons-material/Build';
import EditIcon from '@mui/icons-material/Edit';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import EditNoteIcon from '@mui/icons-material/EditNote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button } from '@mui/material';
import ScheduleInfoSection from './ScheduleInfoSection';
import PendingEditReviewPanel from './PendingEditReviewPanel';
import { getGatheringManageUrl, getGatheringCheckInUrl } from '../../../../utils/environment';
import GatheringDescription from '../../../../shared/components/GatheringDescription';

interface GatheringDetailContentProps {
  gathering: Gathering;
  checkIns: GatheringCheckIn[];
  checkInsLoading: boolean;
  onEditClick?: () => void;
  onRescheduleClick?: () => void;
  onTransferOwnershipClick?: () => void;
  /** Auth token — required to render the pending-edit review actions. */
  token?: string;
  /** Called with the gathering returned after a pending edit is approved or rejected. */
  onPendingEditReviewed?: (gathering: Gathering) => void;
}

// Status color mapping
const statusColorMap: Record<GatheringStatus, string> = {
  [GatheringStatus.NEW]: '#3498db',
  [GatheringStatus.IN_REVIEW]: '#f39c12',
  [GatheringStatus.NEED_MORE_INFO]: '#e74c3c',
  [GatheringStatus.RESCHEDULE]: '#9b59b6',
  [GatheringStatus.DENIED]: '#e74c3c',
  [GatheringStatus.AWAITING_GATHERING_LINK]: '#2ecc71',
  [GatheringStatus.READY_TO_PUBLISH]: '#27ae60',
  [GatheringStatus.PUBLISHED]: '#2ecc71'
};

const GatheringDetailContent: React.FC<GatheringDetailContentProps> = ({ 
  gathering,
  checkIns,
  checkInsLoading,
  onEditClick = () => {},
  onRescheduleClick = () => {},
  onTransferOwnershipClick = () => {},
  token,
  onPendingEditReviewed
}) => {
  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = parseISO(dateString);
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Format time for display
  const formatTime = (timeString: string): string => {
    if (!timeString) return 'N/A';
    
    try {
      // Check if the time already has AM/PM
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }
      
      // Assuming time is in format like "19:00:00"
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const period = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
      
      return `${formattedHour}:${minutes} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration for display
  const formatDuration = (minutes: number): string => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} mins`;
    } else if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    } else {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${remainingMinutes} mins`;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with title, status, and ID */}
      <Box sx={{ 
        mb: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
            {gathering.title}
          </Typography>
          <Chip
            label={gathering.status}
            size="small"
            sx={{
              bgcolor: statusColorMap[gathering.status as GatheringStatus] || 'primary.main',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.85rem'
            }}
          />
          {/* A staged edit keeps the gathering Published, so the status chip
              alone never reveals one — flag it next to the status. */}
          {gathering.pending_edit && (
            <Chip
              icon={<EditNoteIcon sx={{ color: 'white !important' }} />}
              label="Changes pending review"
              size="small"
              sx={{
                bgcolor: '#f39c12',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.85rem'
              }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          ID: {gathering.id}
        </Typography>
        {gathering.created_at && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
            Submitted: {format(new Date(gathering.created_at), 'MMMM d, yyyy')}
          </Typography>
        )}
      </Box>
      
      {/* Action Bar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3,
        pt: 2
      }}>
        <Tooltip 
          title={gathering.source !== 'web' ? 'External gathering: No web app available' : ''}
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(getGatheringManageUrl(gathering.id), '_blank', 'noopener,noreferrer')}
              disabled={gathering.source !== 'web'}
            >
              Manage
            </Button>
          </span>
        </Tooltip>
        <Tooltip 
          title={gathering.source !== 'web' ? 'External gathering: No web app available' : ''}
          arrow
        >
          <span>
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(getGatheringCheckInUrl(gathering.id), '_blank', 'noopener,noreferrer')}
              disabled={gathering.source !== 'web'}
            >
              Check In
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          startIcon={<EditIcon />}
          onClick={onEditClick}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          startIcon={<EventRepeatIcon />}
          onClick={onRescheduleClick}
        >
          Reschedule
        </Button>
      </Box>
      
      {/* Host changes awaiting review (ENG-1097). Sits above the gathering's
          own details because the details below show the LIVE values — the
          staged edit is the thing that needs a decision. */}
      {gathering.pending_edit && token && (
        <PendingEditReviewPanel
          gathering={gathering}
          pendingEdit={gathering.pending_edit}
          token={token}
          onReviewed={onPendingEditReviewed}
        />
      )}

      {/* Schedule Information Section */}
      <ScheduleInfoSection
        gathering={gathering}
        onReschedule={onRescheduleClick}
      />
      
      {/* Main content grid */}
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={3}>
          {/* Event Details */}
          <Grid size={{ xs: 12 }}>
            <Paper 
              elevation={0} 
              variant="outlined" 
              sx={{ 
                p: 3, 
                borderRadius: 2
              }}
            >
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 3 }}>
                Event Details
              </Typography>
              
              <Grid container spacing={3}>
                {/* Image */}
                {gathering.image_url && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box sx={{ 
                      borderRadius: 2,
                      overflow: 'hidden',
                      mb: { xs: 2, md: 0 },
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <img 
                        src={gathering.image_url} 
                        alt={gathering.title} 
                        style={{ 
                          width: '100%',
                          maxHeight: '300px',
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                    </Box>
                  </Grid>
                )}
                
                {/* Event Details */}
                <Grid size={{ xs: 12, md: gathering.image_url ? 8 : 12 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Community, Event Size, Category - All in one row */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'row', 
                      justifyContent: 'space-between', 
                      flexWrap: 'wrap',
                      mb: 1
                    }}>
                      {/* Community */}
                      <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <GroupsIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">Community</Typography>
                        </Box>
                        <Typography variant="body1">
                          {gathering.community || 'N/A'}
                        </Typography>
                      </Box>

                      {/* Event Size */}
                      <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <GroupIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">Event Size</Typography>
                        </Box>
                        <Typography variant="body1">{gathering.event_size || 'N/A'} people</Typography>
                      </Box>
                      
                      {/* Category */}
                      <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                          <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                        </Box>
                        <Typography variant="body1">{gathering.category || 'N/A'}</Typography>
                      </Box>
                      
                      {/* Owner */}
                      {gathering.owner && (
                        <Box sx={{ mb: 0.5, minWidth: '150px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                            <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                fontSize: '0.75rem',
                                bgcolor: 'primary.main'
                              }}
                            >
                              {gathering.owner.display_name?.charAt(0) || 'U'}
                            </Avatar>
                            <Typography variant="body1">
                              {gathering.owner.display_name || 'Unknown'}
                            </Typography>
                            <Tooltip title="Edit Owner">
                              <IconButton
                                size="small"
                                onClick={onTransferOwnershipClick}
                                sx={{ ml: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Gathering Link */}
                    {gathering.luma_link && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Gathering Link
                        </Typography>
                        <Link 
                          href={gathering.luma_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {gathering.luma_link}
                        </Link>
                      </Box>
                    )}
                    
                    {/* Flags */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={gathering.is_public} 
                            disabled 
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Public</Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={gathering.is_members_only} 
                            disabled 
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PeopleIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Members Only</Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={gathering.is_featured} 
                            disabled 
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <StarIcon fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">Featured</Typography>
                          </Box>
                        }
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={gathering.is_fabrik_hosted}
                            disabled
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2">Fabrik Hosted</Typography>
                          </Box>
                        }
                      />

                      <Box sx={{ flexBasis: '100%' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!gathering.is_builder_gathering}
                              disabled
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <BuildIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Typography variant="body2">Builder Gathering</Typography>
                            </Box>
                          }
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                          {gathering.status === GatheringStatus.PUBLISHED
                            ? 'Attendees get the builder follow-up emails instead of the generic sequence. This flag is synced to HubSpot.'
                            : 'Attendees get the builder follow-up emails instead of the generic sequence. It reaches HubSpot when this gathering is published.'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                
                {/* Divider between details and description/notes */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 3 }} />
                </Grid>
                
                {/* Description */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NotesIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  </Box>
                  <GatheringDescription description={gathering.description} />
                </Grid>
                
                {/* Additional Notes */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NotesIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">Additional Notes</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {gathering.additional_notes || 'No additional notes.'}
                  </Typography>
                </Grid>

                {/* Divider before check-ins */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 3 }} />
                </Grid>

                {/* Gathering Check-Ins */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                    <Typography variant="subtitle2" color="text.secondary">
                      Members Gathering Check-Ins
                    </Typography>
                  </Box>

                  {checkInsLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading check-ins…
                    </Typography>
                  ) : !checkIns || checkIns.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No one has checked in yet.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Checked In</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {checkIns.map((ci) => (
                            <TableRow key={ci.id}>
                              <TableCell>
                                {ci.user.first_name} {ci.user.last_name}
                              </TableCell>
                              <TableCell>{ci.user.email}</TableCell>
                              <TableCell>
                                {ci.company_name || '—'}
                              </TableCell>
                              <TableCell>
                                {format(parseISO(ci.timestamp), 'PPpp')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                </Grid>



              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default GatheringDetailContent;